import type { ProviderFoodOrder, ProviderFoodOrderStatus } from "../../lib/api";
import type { TranslationFunction } from "../../lib/preferences";

export const OPEN_ORDER_STATUSES: ProviderFoodOrderStatus[] = ["pending", "preparing", "ready"];

/** Orders older than this without being ready are flagged on the board. */
export const SLOW_ORDER_MINUTES = 15;

export function isOpenOrder(order: Pick<ProviderFoodOrder, "status">) {
  return OPEN_ORDER_STATUSES.includes(order.status);
}

export function orderStatusLabel(status: ProviderFoodOrderStatus, t: TranslationFunction) {
  switch (status) {
    case "pending":
      return t("New");
    case "preparing":
      return t("Preparing");
    case "ready":
      return t("Ready to serve");
    case "delivered":
      return t("Served");
    case "cancelled":
      return t("Cancelled");
  }
}

export function orderStatusTone(status: ProviderFoodOrderStatus) {
  switch (status) {
    case "pending":
      return "warning";
    case "preparing":
      return "info";
    case "ready":
      return "success";
    case "cancelled":
      return "danger";
    default:
      return undefined;
  }
}

/** The one obvious next step for an open order. */
export function nextOrderStep(
  status: ProviderFoodOrderStatus,
  t: TranslationFunction,
): { status: ProviderFoodOrderStatus; label: string } | null {
  switch (status) {
    case "pending":
      return { status: "preparing", label: t("Start preparing") };
    case "preparing":
      return { status: "ready", label: t("Mark ready") };
    case "ready":
      return { status: "delivered", label: t("Mark served") };
    default:
      return null;
  }
}

/** Undo a mis-tap while the order is still open. */
export function previousOrderStep(status: ProviderFoodOrderStatus): ProviderFoodOrderStatus | null {
  switch (status) {
    case "preparing":
      return "pending";
    case "ready":
      return "preparing";
    default:
      return null;
  }
}

export function minutesSince(iso: string, now: number) {
  return Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60000));
}

export function formatWaiting(minutes: number, t: TranslationFunction) {
  if (minutes < 1) return t("just now");
  if (minutes < 60) return t("{count} min", { count: minutes });
  const hours = Math.floor(minutes / 60);
  return t("{hours} h {minutes} min", { hours, minutes: minutes % 60 });
}

export function orderItemsSummary(order: Pick<ProviderFoodOrder, "items">) {
  return order.items.map((item) => `${item.quantity}× ${item.item_name}`).join(", ");
}

export function formatEgp(amount: number, language: string) {
  return new Intl.NumberFormat(language === "ar" ? "ar-EG" : "en-US", {
    style: "currency",
    currency: "EGP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}
